import { Button, Modal, Typography } from "antd";
import days from "dayjs";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { useParams } from "react-router-dom";
import { useCreateSeries } from "../../entities/api";
import AddNewSeriesFormWrapper from "./AddNewSeriesFormWrapper";
import { useNotification } from "~hooks/useNotification";

const validateFormData = ({ data, seasonId }) => {
  const validData = {};

  Object.keys(data)?.forEach((key) => {
    if (data[key]) {
      if (key === "startDate" || key === "endDate") {
        validData[key] = days(data[key]).format("YYYY-MM-DD");
      } else if (key === "stageCode") {
        validData[key] = data[key]?.value ?? null;
      } else {
        validData[key] = data[key];
      }
    }
  });

  validData.seasonId = seasonId;

  return validData;
};

const AddNewSeriesModal = () => {
  const { formatMessage } = useIntl();
  const [openModal, setOpenModal] = useState(false);
  const [seriesQuantity, setSeriesQuantity] = useState(null);
  const formRef = useRef(null);
  const { seasonId } = useParams();
  const [successfullySent, setSuccessfullySent] = useState(0);
  const [failedSent, setFailedSent] = useState(0);
  const { mutateAsync: createFn, isPending } = useCreateSeries();
  const notification = useNotification();

  const handleSetFormRef = useCallback((ref) => {
    formRef.current = ref;
  }, []);

  const handleSetSeriesQuantity = useCallback((value) => setSeriesQuantity(value), []);

  const handleOpenModal = () => {
    setOpenModal((prev) => {
      if (prev) {
        setSeriesQuantity(null);
        setFailedSent(0);
        setSuccessfullySent(0);
      }
      return !prev;
    });
  };

  useEffect(() => {
    if (successfullySent === seriesQuantity) {
      notification.success({
        message: formatMessage({
          id: "series.add.success",
          defaultMessage: "Series added successfully",
        }),
        placement: "bottomRight",
      });
      handleOpenModal();
    }
    if (failedSent > 0 && successfullySent + failedSent === seriesQuantity) {
      notification.error({
        message: formatMessage({
          id: "series.add.failed",
          defaultMessage: "Series add has failed",
        }),
        description: `Only ${successfullySent} of ${seriesQuantity} were added successfully`,
        placement: "bottomRight",
      });
      handleOpenModal();
    }
  }, [successfullySent, failedSent, seriesQuantity, formatMessage]);

  const handleSubmitForm = async () => {
    let validData = {};
    try {
      const data = await formRef.current.validateFields();
      validData = validateFormData({ data, seasonId });
    } catch (e) {
      console.error(e);
      return;
    }

    for (let i = 0; i < seriesQuantity; i++) {
      try {
        const res = await createFn({
          ...validData,
          seriesCode: Math.random().toString(36).slice(2),
        });

        if (res?.data?.length) {
          setSuccessfullySent((prev) => prev + 1);
        }
      } catch (e) {
        setFailedSent((prev) => prev + 1);
        console.error(e);
      }
    }
  };

  return (
    <>
      <Button
        onClick={handleOpenModal}
        icon={<i className="fas fa-plus" style={{ marginRight: "5px" }}/>}
        type="primary"
      >
        {formatMessage({
          id: "series.add",
          defaultMessage: "Add new Series",
        })}
      </Button>
      <Modal
        width={600}
        open={openModal}
        destroyOnClose={true}
        onCancel={handleOpenModal}
        okText={"Add"}
        onOk={handleSubmitForm}
        okButtonProps={{
          loading: isPending,
        }}
      >
        <Typography.Title level={4}>
          {formatMessage({
            id: "series.add",
            defaultMessage: "Add new Series",
          })}
        </Typography.Title>
        <AddNewSeriesFormWrapper
          formRef={formRef}
          handleSetFormRef={handleSetFormRef}
          seriesQuantity={seriesQuantity}
          handleSetSeriesQuantity={handleSetSeriesQuantity}
        />
      </Modal>
    </>
  );
};

export default AddNewSeriesModal;
